import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useRef } from "react";
import { useTranslate } from "react-admin";
import { useCopyFeedback } from "./useCopyFeedback";

const useStyles = makeStyles({
	wrapper: {
		cursor: "pointer",
		display: "inline-flex",
		"&:hover": {
			opacity: 0.8,
		},
	},
});

/**
 * Wraps any child component so that clicking it copies text to clipboard.
 * Pass `source`, `label`, and `sortable` on the wrapper (not the child)
 * so the Datagrid can read them for column headers.
 *
 * Usage:
 *   <CopyableWrapper source="available_weight" label="Weight" sortable>
 *       <FunctionField render={(record) => record.available_weight} />
 *   </CopyableWrapper>
 */
export const CopyableWrapper = ({ value, children, source, label, sortable, ...rest }) => {
	const classes = useStyles();
	const translate = useTranslate();
	const ref = useRef(null);
	const { copied, copy } = useCopyFeedback();

	const handleClick = (e) => {
		e.stopPropagation();
		copy(value ?? ref.current?.textContent);
	};

	// Forward Datagrid-injected props (record, basePath, etc.) + source to the child
	const childProps = { ...rest, source };

	return (
		<Tooltip title={copied ? translate("osfc.buttons.copy.success") : translate("osfc.buttons.copy.title")}>
			<span ref={ref} className={classes.wrapper} onClick={handleClick}>
				{React.isValidElement(children)
					? React.cloneElement(children, childProps)
					: children}
			</span>
		</Tooltip>
	);
};

CopyableWrapper.defaultProps = {
	addLabel: true,
};
